import {
  FormProvider,
  Resolver,
  useFieldArray,
  useForm,
  useWatch
} from 'react-hook-form';
import React, { FC, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import * as yup from 'yup';

import { PlusIcon, TrashIcon } from '@assets/icons';
import { translate } from '@utils/locale';
import { CustomModal, ImagePreviewCard, RhfTextField } from '@components';
import {
  API_RESPONSE_STATUS,
  IMAGE_FORMATS,
  SHORT_FIELD_MAX_LENGTH
} from '@constants/common';
import { generateFileUrl } from '@utils/common';
import { showGenericErrorToast } from '@utils/error';
import { ToastTypes } from '@type/toast';
import displayToast from '@components/toast/customToast';
import FormDualButton from '@components/form-dual-button/FormDualButton';
import FileUploader from '@containers/file-uploader/FileUploader';

import {
  CompetitorsSectionPropType,
  CreateCompetitorFormFieldValues,
  createCompetitorFormType
} from '../types';
import {
  CreateCompetitorFormDefaultFieldValues,
  SectionMapper
} from '../constants';
import {
  useAddCompetitorMutation,
  useDeleteCompetitorMutation,
  useUpdateCompetitorMutation
} from '../api';

const CompetitorsSection: FC<CompetitorsSectionPropType> = ({
  editMode,
  updateEditMode,
  disableEdit = false,
  getResetFunction,
  getResetCompetitors,
  companyDetails
}) => {
  const { companyId } = useParams();

  const [isCancelClicked, setIsCancelClicked] = useState<boolean>(true);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState<
    'discard' | 'save' | undefined
  >();
  const [newEditIndex, setNewEditIndex] = useState<number | undefined>();
  const [competitorsEditIndex, setCompetitorsEditIndex] = useState<
    number | undefined
  >();
  const [createCompetitor, { isLoading }] = useAddCompetitorMutation();
  const [deleteCompetitor, { isLoading: isDeleteCompetitorLoading }] =
    useDeleteCompetitorMutation();
  const [updateCompetitor, { isLoading: isUpdateCompetitorLoading }] =
    useUpdateCompetitorMutation();

  const isEditMode = editMode === SectionMapper.competitors;

  const schema = yup.object({
    products: yup.array().of(
      yup.object().shape({
        id: yup.string(),
        companyName: yup.string()
      })
    )
  });

  const methods = useForm<CreateCompetitorFormFieldValues>({
    resolver: yupResolver(
      schema
    ) as never as Resolver<CreateCompetitorFormFieldValues>,
    defaultValues: CreateCompetitorFormDefaultFieldValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const {
    control,
    getValues,
    trigger,
    reset,
    setValue,
    clearErrors,
    formState: { errors }
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'competitors'
  });

  const competitorsList = useWatch({ control: control, name: 'competitors' });

  const isSaveDisabled = (index: number) => {
    const competitors = competitorsList?.[index];
    return !(
      competitors?.id ||
      competitors?.companyName ||
      competitors?.companyLogo?.path
    );
  };

  const showImageUploader = (index: number) =>
    !getValues(`competitors.${index}.companyLogo`)?.path;

  const showDeleteIcon = (index: number) =>
    getValues(`competitors.${index}.id`)
      ? isEditMode && competitorsEditIndex === index
      : false;

  const handleAddNewCompetitor = () => {
    append({
      id: '',
      companyName: '',
      companyLogo: { file: undefined, path: '' }
    });
  };

  const onEditSaveButtonClick = async (index: number) => {
    if (!editMode) {
      setCompetitorsEditIndex(index);
      updateEditMode?.(SectionMapper.competitors);
      return;
    }
    if (
      editMode !== SectionMapper.competitors ||
      (editMode === SectionMapper.competitors && index !== competitorsEditIndex)
    ) {
      setNewEditIndex(index);
      setConfirmModalOpen('discard');
      return;
    }
    const isFormValid = await trigger();
    if (isFormValid) {
      setConfirmModalOpen('save');
    }
  };

  const onClickCancelButton = () => {
    setIsCancelClicked(true);
    setConfirmModalOpen('discard');
  };

  const onDiscardDetails = () => {
    const values = getValues();
    reset(getResetFunction(editMode)(values));
    isCancelClicked
      ? updateEditMode?.()
      : updateEditMode?.(SectionMapper.competitors);
    setCompetitorsEditIndex(newEditIndex);
    setNewEditIndex(undefined);
    setConfirmModalOpen(undefined);
  };

  const onUpdateDetails = () => {
    const competitorData =
      getValues()?.competitors?.[competitorsEditIndex as number];
    if (!competitorData?.id) onCreateCompetitor(competitorData);
    else onUpdateCompetitor(competitorData as createCompetitorFormType);
  };

  const onCreateCompetitor = async (
    competitorData:
      | {
          id: string;
          companyName: string;
          companyLogo: {
            file: File | undefined;
            path: string;
          };
        }
      | undefined
  ) => {
    if (competitorData) {
      if (competitorData.companyLogo.path || competitorData.companyName)
        try {
          const response = await createCompetitor({
            company_id: companyId ?? '',
            payload: {
              logo_path: competitorData.companyLogo.path,
              name: competitorData.companyName
            }
          }).unwrap();
          if (response.status === API_RESPONSE_STATUS.OK) {
            displayToast(ToastTypes.SUCCESS, 'Competitor Added Successfully');
            updateEditMode?.();
          } else {
            displayToast(
              ToastTypes.ERROR,
              response.error?.details?.[0] ?? 'Failed to add Competitor '
            );
          }
        } catch (err) {
          showGenericErrorToast(err);
        }
    }
    setConfirmModalOpen(undefined);
  };

  const onUpdateCompetitor = async (competitorData: {
    id: string;
    companyName: string;
    companyLogo: {
      file: File | undefined;
      path: string;
    };
  }) => {
    try {
      const response = await updateCompetitor({
        competitor_id: competitorData.id,
        company_id: companyId ?? '',
        payload: {
          logo_path: competitorData.companyLogo.path,
          name: competitorData.companyName
        }
      }).unwrap();
      if (response.status === API_RESPONSE_STATUS.OK) {
        displayToast(ToastTypes.SUCCESS, 'Competitor updated Successfully');
        updateEditMode?.();
      } else {
        displayToast(
          ToastTypes.ERROR,
          response.error?.details?.[0] ?? 'Failed to update Competitor '
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
    setConfirmModalOpen(undefined);
  };

  const onDeleteCompetitor = async (id: string) => {
    try {
      const response = await deleteCompetitor({
        competitor_id: id,
        company_id: companyId ?? ''
      }).unwrap();
      if (response.status === API_RESPONSE_STATUS.OK) {
        updateEditMode?.();
        remove(competitorsEditIndex);
        displayToast(ToastTypes.SUCCESS, 'Competitor deleted Successfully');
      } else {
        displayToast(
          ToastTypes.ERROR,
          response.error?.details?.[0] ?? 'Failed to delete Competitor '
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  const handleRemoveCompetitor = async (index: number) => {
    const competitorId = getValues(`competitors.${index}.id`);
    if (competitorId) {
      onDeleteCompetitor(competitorId);
    } else remove(index);
  };

  const deleteFile = (value: string) => {
    setValue(value as `competitors.${number}.companyLogo`, {
      file: undefined,
      path: ''
    });
  };

  const getImageUrl = (index: number) => {
    const fieldValue = competitorsList[index]?.companyLogo;
    return fieldValue ? generateFileUrl(fieldValue.path, fieldValue.file) : '';
  };

  const getFileName = (value: string) =>
    getValues(value as `competitors.${number}.companyLogo`).file?.name;

  const onFileUploadSuccess = (file: File, path: string, index: number) => {
    setValue(`competitors.${index}.companyLogo`, {
      file: file,
      path: path
    });
    clearErrors(`competitors.${index}.companyLogo`);
  };

  useEffect(() => {
    if (companyDetails?.competitors?.length)
      reset(getResetCompetitors(getValues()));
  }, [companyDetails]);

  return (
    <div className="pt-9">
      <div className="flex gap-2 justify-between pb-5">
        <div className="text-xl font-semibold leading-8">Competitors</div>
      </div>
      <FormProvider {...methods}>
        {fields.map((competitors, index) => (
          // eslint-disable-next-line tailwindcss/migration-from-tailwind-2
          <div
            key={competitors.id}
            className="p-4 mb-4 bg-slate-50 bg-opacity-[0.5] rounded border border-lightGrey border-opacity-[0.5]">
            <div className="flex gap-2 justify-between pb-5 ">
              <div className="flex flex-row gap-7 items-center text-base font-normal uppercase ">
                {translate('company.competitors.title', { count: index + 1 })}
                {showDeleteIcon(index) && (
                  <TrashIcon
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => handleRemoveCompetitor(index)}
                  />
                )}
              </div>
              <FormDualButton
                enableEdit={!disableEdit}
                showCancelOrSaveButton={
                  isEditMode && competitorsEditIndex === index
                }
                isSaveDisabled={isSaveDisabled(index)}
                onClickCancelButton={onClickCancelButton}
                onClickEditButton={() => onEditSaveButtonClick(index)}
              />
            </div>
            <div className="flex gap-6 w-full lg:w-[70%]">
              <RhfTextField
                name={`competitors.${index}.companyName`}
                label={translate('company.competitors.companyName')}
                placeholder={translate(
                  'company.placeholders.competitorsCompany'
                )}
                description={translate('company.competitors.nameDescription')}
                control={control}
                errors={{
                  [`competitors.${index}.companyName`]:
                    errors.competitors?.[index]?.companyName
                }}
                wrapperClass="mb-5 w-full mt-8"
                isDisabled={!(isEditMode && competitorsEditIndex === index)}
                maxLength={SHORT_FIELD_MAX_LENGTH}
                showCharacterLimit
              />
              {/* Logo section */}
              <div className="w-full">
                <div className="flex flex-row items-center pb-2 text-base font-normal text-black/60">
                  {translate('company.competitors.companyLogo')}
                </div>
                {showImageUploader(index) ? (
                  <FileUploader
                    name="companyLogo"
                    enableCrop={true}
                    sizeInMb={3}
                    supportedFormats={IMAGE_FORMATS}
                    fileUploadSuccessHandler={(file, path) =>
                      onFileUploadSuccess(file, path, index)
                    }
                    deleteFileHandler={() =>
                      deleteFile(`competitors.${index}.companyLogo`)
                    }
                    errors={errors}
                    previousFile={
                      getValues(`competitors.${index}.companyLogo`)?.file
                    }
                    aspectRatio={1}
                    imageInfo={translate('deals.companyLogoInfoText')}
                    showPreview={true}
                    isDisabled={!(isEditMode && competitorsEditIndex === index)}
                    customUploadText={translate(
                      'company.placeholders.companyLogo'
                    )}
                    description={translate('company.competitors.companyLogo')}
                  />
                ) : (
                  <ImagePreviewCard
                    name="companyLogo"
                    isEdit={isEditMode && competitorsEditIndex === index}
                    handleDeleteClick={() =>
                      deleteFile(`competitors.${index}.companyLogo`)
                    }
                    imageUrl={getImageUrl(index)}
                    fileName={getFileName(`competitors.${index}.companyLogo`)}
                    description={translate('company.competitors.companyLogo')}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </FormProvider>
      {isConfirmModalOpen && (
        <CustomModal
          isModalOpen={!!isConfirmModalOpen}
          header={
            isConfirmModalOpen === 'discard'
              ? translate('modals.discardPopupTitle')
              : translate('modals.saveInfoTitle')
          }
          primaryButtonLabel={
            isConfirmModalOpen === 'discard'
              ? translate('modals.confirm')
              : translate('update')
          }
          secondaryButtonVariant="text"
          secondaryButtonLabel={translate('modals.cancelButtonTitle')}
          onChangePrimaryButton={
            isConfirmModalOpen === 'discard'
              ? onDiscardDetails
              : onUpdateDetails
          }
          onChangeSecondaryButton={() => setConfirmModalOpen(undefined)}
          isPrimaryButtonLoading={
            isLoading || isUpdateCompetitorLoading || isDeleteCompetitorLoading
          }
          isPrimaryButtonDisabled={
            isLoading || isUpdateCompetitorLoading || isDeleteCompetitorLoading
          }
        />
      )}
      <div className="group">
        {!disableEdit && (
          <Button
            variant="outlined"
            color="info"
            size="small"
            className="group-hover:text-white group-hover:bg-brightBlue"
            startIcon={<PlusIcon />}
            disabled={
              editMode !== undefined ||
              (companyDetails?.competitors?.length ?? 0) + 1 === fields.length
            }
            onClick={handleAddNewCompetitor}>
            {translate('company.competitors.addCompetitor')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CompetitorsSection;
